import { getTraceId, type HeaderLike } from "./traceId";

export interface NetworkErrorReport {
  source: string;
  status: number;
  method: string;
  requestUrl: string;
  pageUrl: string;
  timestamp: string;
  orgId?: string;
  traceId?: string;
  transactionIds: string[];
  walletIds: string[];
  requestBody?: string;
  responseBody?: string;
  requestContext?: string;
  operationName?: string;
}

export interface NetworkErrorBuildInput {
  source: string;
  status: number;
  method?: string;
  requestUrl: string;
  pageUrl?: string;
  requestBody?: unknown;
  responseBody?: unknown;
  headers?: HeaderLike;
  orgIdGetter?: () => string | null | undefined;
  requestContext?: string;
  operationName?: string;
}

const MAX_FIELD_CHARS = 30_000;
const ORG_KEYS = new Set(["orgid", "organizationid"]);
const TXN_KEYS = new Set([
  "txnid",
  "txnids",
  "transactionid",
  "transactionids",
  "txid",
  "txids",
  "txhash",
  "hash",
]);
const WALLET_RE = /^walletids?$/i;
const SECRET_KEY_RE =
  /(authorization|password|secret|token|apikey|api(passphrase|key)|cookie|jwt|bearer|privatekey|mnemonic|passphrase|credential)/i;
const JWT_VALUE_RE =
  /\b[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\b/g;
const BEARER_VALUE_RE = /\bBearer\s+[A-Za-z0-9._~+/=-]{10,}\b/gi;

function normKey(k: string) {
  return k.replace(/[_-]/g, "").toLowerCase();
}

function parse(value: unknown): unknown {
  if (typeof value !== "string") return value;
  const t = value.trim();
  if (!t || (!t.startsWith("{") && !t.startsWith("["))) return value;
  try {
    return JSON.parse(t);
  } catch {
    return value;
  }
}

function redactString(value: string): string {
  return value
    .replace(BEARER_VALUE_RE, "Bearer [REDACTED]")
    .replace(JWT_VALUE_RE, "[REDACTED]");
}

/** Recursively mask values whose key looks sensitive. Returns a new object/array. */
function redact(value: unknown, depth = 0, seen?: WeakSet<object>): unknown {
  if (!value || typeof value !== "object") {
    if (typeof value === "string") return redactString(value);
    if (typeof value === "bigint") return value.toString();
    return value;
  }
  if (depth > 6) return "[MAX_DEPTH]";

  const obj = value as object;
  const refs = seen || new WeakSet<object>();
  if (refs.has(obj)) return "[CIRCULAR]";
  refs.add(obj);

  if (Array.isArray(value)) return value.map((v) => redact(v, depth + 1, refs));
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    out[k] = SECRET_KEY_RE.test(normKey(k))
      ? "[REDACTED]"
      : redact(v, depth + 1, refs);
  }
  return out;
}

function truncate(value: unknown): string | undefined {
  if (value === undefined || value === null) return undefined;
  const redacted =
    typeof value === "object" ? redact(value) : redact(parse(value));
  let text: string;
  if (typeof redacted === "string") {
    text = redacted;
  } else {
    try {
      text = JSON.stringify(redacted, null, 2);
    } catch {
      text = String(redacted);
    }
  }
  return text.length > MAX_FIELD_CHARS
    ? `${text.slice(0, MAX_FIELD_CHARS)}...[truncated]`
    : text;
}

function collect(
  value: unknown,
  match: (k: string) => boolean,
  out: string[],
  depth = 0,
): void {
  if (!value || typeof value !== "object" || depth > 5) return;
  if (Array.isArray(value)) {
    value.forEach((v) => collect(v, match, out, depth + 1));
    return;
  }
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    if (match(k)) {
      const list = Array.isArray(v) ? v : [v];
      list.forEach((x) => {
        if (x != null && !out.includes(String(x))) out.push(String(x));
      });
    }
    collect(v, match, out, depth + 1);
  }
}

function extractOrgId(
  url: string,
  req: unknown,
  res: unknown,
  getter?: () => string | null | undefined,
): string | undefined {
  const m = url.match(/\/orgs\/([^/?#]+)/i);
  if (m && m[1]) return m[1];
  try {
    const u = new URL(url, window.location.origin);
    const q =
      u.searchParams.get("orgId") || u.searchParams.get("organizationId");
    if (q) return q;
  } catch {
    // relative url
  }
  const found: string[] = [];
  collect(parse(req), (k) => ORG_KEYS.has(normKey(k)), found);
  collect(parse(res), (k) => ORG_KEYS.has(normKey(k)), found);
  return found[0] || (getter ? getter() || undefined : undefined);
}

/** GraphQL error detection - ONLY the spec `errors` array. Do not treat a generic `error` string as an error. */
export function hasGraphQLErrors(value: unknown): boolean {
  const v = parse(value);
  if (!v || typeof v !== "object") return false;
  const r = v as Record<string, unknown>;
  return Array.isArray(r.errors) && r.errors.length > 0;
}

export function buildNetworkErrorReport(
  input: NetworkErrorBuildInput,
): NetworkErrorReport {
  const txn: string[] = [];
  const wallet: string[] = [];
  collect(parse(input.requestBody), (k) => TXN_KEYS.has(normKey(k)), txn);
  collect(parse(input.responseBody), (k) => TXN_KEYS.has(normKey(k)), txn);
  collect(parse(input.requestBody), (k) => WALLET_RE.test(k), wallet);
  collect(parse(input.responseBody), (k) => WALLET_RE.test(k), wallet);
  return {
    source: input.source,
    status: input.status,
    method: (input.method || "GET").toUpperCase(),
    requestUrl: input.requestUrl,
    pageUrl: input.pageUrl || window.location.href,
    timestamp: new Date().toISOString(),
    orgId: extractOrgId(
      input.requestUrl,
      input.requestBody,
      input.responseBody,
      input.orgIdGetter,
    ),
    traceId: getTraceId({ body: input.responseBody, headers: input.headers }),
    transactionIds: [...new Set(txn)],
    walletIds: [...new Set(wallet)],
    requestBody: truncate(input.requestBody),
    responseBody: truncate(input.responseBody),
    requestContext: input.requestContext,
    operationName: input.operationName,
  };
}

function joinList(v: string[]) {
  return v.length ? v.join(", ") : "Not captured";
}

export function formatNetworkErrorReport(r: NetworkErrorReport): string {
  const lines = [
    "Bitwave network error",
    `Date/Time: ${new Date(r.timestamp).toLocaleString()} (${r.timestamp})`,
    `Source: ${r.source}`,
    `Org ID: ${r.orgId || "Not captured"}`,
    `Status: ${r.status}`,
    `Method: ${r.method}`,
    `URL: ${r.requestUrl}`,
    `Page URL: ${r.pageUrl}`,
    `Transaction ID(s): ${joinList(r.transactionIds)}`,
    `Wallet ID(s): ${joinList(r.walletIds)}`,
    `Trace ID: ${r.traceId || "Not captured"}`,
  ];
  if (r.requestContext) lines.push(`Request Context: ${r.requestContext}`);
  if (r.operationName) lines.push(`Operation Name: ${r.operationName}`);
  lines.push(
    `Payload:\n${r.requestBody || "Not captured"}`,
    `Response:\n${r.responseBody || "Not captured"}`,
  );
  return lines.join("\n");
}

/** Stable identity for dedup. */
export function networkErrorSignature(r: NetworkErrorReport): string {
  return [
    r.source,
    r.status,
    r.method,
    r.requestUrl,
    r.orgId || "",
    r.traceId || "",
    r.responseBody || "",
  ].join("|");
}

/** Machine-readable code api-svc and the api-gateway return when an org's sign-in policy is not met. */
export const IDENTITY_PROVIDER_REQUIRED_CODE = "IDENTITY_PROVIDER_REQUIRED";

export interface AllowedIdentityProvider {
  type: string;
  domain?: string;
}

export interface IdentityProviderRequired {
  orgId?: string;
  allowedIdentityProviders: AllowedIdentityProvider[];
}

function readIdentityProviderRequired(
  obj: unknown,
): IdentityProviderRequired | undefined {
  if (typeof obj !== "object" || obj === null) return undefined;
  const o = obj as {
    code?: unknown;
    orgId?: unknown;
    allowedIdentityProviders?: unknown;
  };
  if (o.code !== IDENTITY_PROVIDER_REQUIRED_CODE) return undefined;
  const providers = Array.isArray(o.allowedIdentityProviders)
    ? o.allowedIdentityProviders.filter(
        (p): p is AllowedIdentityProvider =>
          typeof p === "object" &&
          p !== null &&
          typeof (p as { type?: unknown }).type === "string",
      )
    : [];
  return {
    orgId: typeof o.orgId === "string" ? o.orgId : undefined,
    allowedIdentityProviders: providers,
  };
}

/**
 * Recognize an IDENTITY_PROVIDER_REQUIRED refusal in any of its wire shapes:
 *  - api-svc REST: 403 `{code, message, orgId, allowedIdentityProviders}`
 *  - api-gateway:  403 `{error: 'access_denied', code, error_description, orgId, allowedIdentityProviders}`
 *  - GraphQL:      200 `{errors: [{extensions: {code, orgId, allowedIdentityProviders}}]}`
 * Returns the org and its allowed providers, or undefined when this is not that refusal.
 */
export function identityProviderRequired(
  status: number,
  body: unknown,
  isGraphQL = false,
): IdentityProviderRequired | undefined {
  if (status === 403) {
    const direct = readIdentityProviderRequired(body);
    if (direct) return direct;
  }
  if (isGraphQL && typeof body === "object" && body !== null) {
    const errors = (body as { errors?: unknown }).errors;
    if (Array.isArray(errors)) {
      for (const e of errors) {
        const found = readIdentityProviderRequired(
          (e as { extensions?: unknown })?.extensions,
        );
        if (found) return found;
      }
    }
  }
  return undefined;
}
